import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { forwardRef, type ReactNode } from 'react'
import { IconTile } from 'shared/components/icon-tile'
import { twMerge } from 'tailwind-merge'

interface NavigationCardProps extends Omit<
  useRender.ComponentProps<'button'>,
  'children' | 'title'
> {
  title: string
  subtitle?: string
  icon?: ReactNode
  subtitleIcon?: ReactNode
  selected?: boolean
}

const NavigationCard = forwardRef<HTMLButtonElement, NavigationCardProps>(
  ({ icon, subtitle, subtitleIcon, title, selected, className, render, ...props }, ref) => {
    const defaultProps = {
      type: 'button' as const,
      className: twMerge(
        'group flex w-full items-start gap-3 rounded-lg py-2 pr-2 pl-3 text-start transition-base',
        selected && 'bg-bg-accent-light',
        className,
      ),
      children: (
        <>
          <IconTile tone={selected ? 'white' : 'outline'}>{icon}</IconTile>
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
            <span className="text-text-primary w-full text-base font-medium [word-break:break-word]">
              {title}
            </span>
            {subtitle && (
              <span className="flex w-full items-center gap-1">
                {subtitleIcon && (
                  <span className="text-icon-tertiary flex size-4 shrink-0 items-center justify-center [&_svg]:size-4">
                    {subtitleIcon}
                  </span>
                )}
                <span className="text-text-quaternary min-w-0 flex-1 text-sm [word-break:break-word]">
                  {subtitle}
                </span>
              </span>
            )}
          </div>
        </>
      ),
    }

    return useRender({
      defaultTagName: 'button',
      props: mergeProps<'button'>(defaultProps, props),
      ref,
      render,
    })
  },
)

NavigationCard.displayName = 'NavigationCard'

export { NavigationCard }
export type { NavigationCardProps }
